import React from 'react';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import QuestionNode from './question-node';
import AnswersNode from './answers-node';
import ButtonNode from './button-node';
import FeedbackItem from './feedback-item-node';
import {filterNodeProperties} from '../../../../utilities/filter-node-properties';
import GraphicAnswerNode from './graphic-answer-node';
import * as api from '../../../../utilities/api';
import {isEmpty} from '../../../../utilities/validation';
import {saveAnswer} from '../../../../actions/jre';
import {formatErrors} from '../../../../actions/errors';
import {handleEvent} from '../../../../events/event-handler';

function shouldAcceptAnyAnswer(answerNodes) {
    /**
     * When all answers have correct attribute === 0 / 1; it means any answer selection should be marked as correct
     * Carbon Footprint 3.2.1 p706 example for all correct === '1'
     * */
    return (
        (answerNodes.filter(e => e.attr.correct === '0').length === answerNodes.length)
        || (answerNodes.filter(e => e.attr.correct === '1').length === answerNodes.length)
    );
}

const getDataFromMcNode = function(node) {
    const keys = ['id', 'title', 'attempts', 'enabled', 'required', 'output', 'variable'];
    return filterNodeProperties(node, keys);
};

/* We think that we may need modified/custom feedback data retrieved in the future for which the class will be
   responsible. That's the reason this its not within the FeedbackItem class. */
const getDataFromFeedbackNode = function(node) {
    const keys = ['id', 'title', 'audio', 'feedback_type'];
    return filterNodeProperties(node, keys, { val: node.val });
};

const getStateFromProps = function(props = {}) {
    const { content = {} } = props;
    const { node = null } = content;

    let answersSelected = {};
    let answerNodes = [];
    let graphicNodes = [];
    let allowMultiSelect = false;
    let acceptAnyAnswer = false;

    const answerNode = props.node.childNamed('answers_node');
    const graphicNode = props.node.childNamed('graphic_answers_node');

    const mcType = answerNode ? 'answerNode' : 'graphicNode';

    if (answerNode) {
        answerNodes = answerNode.childrenNamed('answer_node');

        answersSelected = answerNodes.reduce(
            (selected, n) => {
                return {...selected, [n.attr.id]: false};
            }, {}
        );

        if (props.node.attr.output === 'index') {
            answerNodes.forEach(function (answer, idx) {
                answer.index = idx;
            });
        }
        //when all answers are correct="false", we want to accept any answer as correct (MO.PERF.3.1.0 - 113)
        acceptAnyAnswer = shouldAcceptAnyAnswer(answerNodes);

    } else if (graphicNode) {
        if (graphicNode.attr.allow_multi_select === '1') {
            allowMultiSelect = true;
        }
        graphicNodes = graphicNode.childrenNamed('graphic_answer_node');

        answersSelected  = graphicNodes.reduce(
            (selected, n) => {
                return {...selected, [n.attr.id]: false};
            }, {}
        );

        if (props.node.attr.output === 'index') {
            graphicNodes.forEach(function (graphicAnswer, idx) {
                graphicAnswer.index = idx;
            });
        }

        //when all answers are correct="false", we want to accept any answer as correct
        acceptAnyAnswer = shouldAcceptAnyAnswer(graphicNodes);
    }

    return {
        ...getDataFromMcNode(props.node),
        node,
        overlayMask: true,
        numOfAttempts: 0,
        mcCompleted: false,
        currentFeedbackItem: null,
        allowMultiSelect,
        answerNodes,
        graphicNodes,
        answersSelected,
        mcType,
        acceptAnyAnswer,
    };
};

class McNode extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            ...getStateFromProps(props),
            answers: {},
            audioEnded: false,
        };

        this.audioEnded = this.audioEnded.bind(this);
        this.saveStudentAnswer = this.saveStudentAnswer.bind(this);
        this.updateAnswerProgress = this.updateAnswerProgress.bind(this);
        this.setCorrect = this.setCorrect.bind(this);
        this.onInteractionComplete = this.onInteractionComplete.bind(this);
        this.disableOverlayMask = this.disableOverlayMask.bind(this);
        this.areAllMcNodesCompleted = this.areAllMcNodesCompleted.bind(this);
    }

    componentDidMount() {
        const {disableJREAppPlayerContainerButtons} = this.props;
        disableJREAppPlayerContainerButtons('requiredNodeNotCompleted', 'McNode componentDidMount ');
        document.addEventListener('jre-audio-ended', this.audioEnded);
        document.addEventListener('jre-mc-correct', this.setCorrect);
        document.addEventListener('jre-mc-enabled', this.disableOverlayMask);
        document.addEventListener('jre-disable-overlay', this.disableOverlayMask);
    }

    // NOTE!!!! MCNode will completely reset its state any time new props are passed to MCNode with a new node!!!!!!
    UNSAFE_componentWillReceiveProps(nextProps) {
        if (this.props.node !== nextProps.node) {
            this.setState({
                ...getStateFromProps(nextProps)
            });
        }
    }

    componentWillUnmount() {
        const {enableJREAppPlayerContainerButtons,} = this.props;
        enableJREAppPlayerContainerButtons('requiredNodeNotCompleted', 'McNode componentWillUnmount');
        document.removeEventListener('jre-mc-enabled', this.disableOverlayMask);
        document.removeEventListener('jre-audio-ended', this.audioEnded);
        document.removeEventListener('jre-mc-correct', this.setCorrect);
        document.removeEventListener('jre-disable-overlay', this.disableOverlayMask);
    }

    // eslint-disable-next-line no-unused-vars
    componentDidUpdate(prevProps, prevState, snapshot) {
        /*
        *  if allMcNodesCompleted not true, don't enable overlay mask
        * without componentDidUpdate, when student completes last mc-node question changes won't register
        * */
        const {completedMcNodes, enableJREAppPlayerContainerButtons, disableJREAppPlayerContainerButtons} = this.props;
        const {audioEnded} = this.state;
        if (completedMcNodes !== prevProps.completedMcNodes) {
            const allMcNodesCompleted = this.areAllMcNodesCompleted();
            this.setState({
                    /*
                    *   if !audioEnded, it means initial page audio has not finished playing, thus display overlayMask
                    *   once user submits answers for all mc-node(s) on page allMcNodesCompleted === true, disable mc-node
                    *  */
                    overlayMask: allMcNodesCompleted || !audioEnded,
                }, () => allMcNodesCompleted ?
                enableJREAppPlayerContainerButtons('requiredNodeNotCompleted', 'McNode componentDidUpdate') :
                disableJREAppPlayerContainerButtons('requiredNodeNotCompleted', 'McNode componentDidUpdate')
            );
        }
    }

    disableOverlayMask(){
        this.setState({overlayMask: false});
    }

    audioEnded() {
        this.setState({audioEnded: true});
        this.setState({overlayMask: false});   
    }

    feedbackAudioEnded() {
        if (!this.state.mcCompleted) {
            this.setState({overlayMask: false});
        }
    }

    saveStudentAnswer(key, val = '') {
        const {saveAnswer,users} = this.props;
        let {answers = {}} = this.state;

        if (typeof key !== 'undefined' && !isEmpty(key) && !isEmpty(val)) {
            answers[key] = val;
            this.setState({ answers: answers });
            saveAnswer({[key]: val},users);
        }
    }

    updateAnswerProgress() {
        const {moduleID, sessionNum, pageNum, formatErrors,users} = this.props;
        let {answers = {}} = this.state;

        let sessionNumber = 1;
        if(parseInt(sessionNum) > 0){
            sessionNumber = sessionNum;
            if(sessionNumber > 7) sessionNumber = 7;
        }

        let formattedAnswers = [];
        Object.entries(answers).map(studentAnswer => {
            if(!isEmpty(studentAnswer[0]) && !isEmpty(studentAnswer[1])) {
                let tempObject = {};
                tempObject[studentAnswer[0]] = studentAnswer[1];
                formattedAnswers.push(tempObject);
            }
        });

        let payload = {
            moduleId: moduleID,
            sessionNum: sessionNumber,
            pageNum: pageNum,
            completed: false,
            answers: JSON.stringify(formattedAnswers)
        };

        window.contentparams = JSON.parse(localStorage.getItem('contentparams'));
        api.setModuleProgress(payload,window.contentparams)
            .catch(err => formatErrors(err));
    }

    setCorrect(e) { //For the case where an answer is dynamically set to 'correct' ex. MO.SUDE.3.0.1
        const answerNode = this.props.node.childNamed('answers_node');
        let answerNodes = [];

        if (answerNode) {
            answerNodes = answerNode.childrenNamed('answer_node');

            answerNodes.map((answer) =>
                !(answer.name === 'answer_node') ?
                    null :
                    answer.attr.id === e.detail.target ?
                        answer.attr.correct = '1' : null
            );
            this.setState({acceptAnyAnswer: shouldAcceptAnyAnswer(answerNodes)});
        }
    }

    fetchAnswerFeedbackNode(answerNode, correct = null) {
        const {acceptAnyAnswer} = this.state;
        const hasMoreAttempts = this.hasMoreAttempts();
        let return_feedback;
        // console.log(answerNode);
        if(!hasMoreAttempts && answerNode != null && isEmpty(answerNode.childNamed('feedback_item_node'))
            && isEmpty(this.props.node.childNamed('feedback_node')) && acceptAnyAnswer){
            console.warn('A Feedback Node Does Not Exist');
            return null;
        }

        // Some answer nodes will have a feedback item as a child. If it exists, use it as the feedback.
        if (answerNode && hasMoreAttempts) {
            const feedbackItemNode = answerNode.childNamed('feedback_item_node') || null;
            if (feedbackItemNode) {
                return feedbackItemNode;
            }
        }

        const feedback_nodes = this.props.node.childNamed('feedback_node').childrenNamed('feedback_item_node');

        // Comparing against "title" is unreliable because the text is actually inconsistent.
        if (correct === true) {
            if (acceptAnyAnswer) { // if acceptAnyAnswer is true, the correct feedback is sometimes incorrect_final (MO.PERF.3.1.0 - 113)
                return_feedback = feedback_nodes.find(n => n.attr.feedback_type === 'correct' || n.attr.feedback_type === 'incorrect_final');
            } else {
                return_feedback = feedback_nodes.find(n => n.attr.feedback_type === 'correct');
            }
        } else if (correct === false) {
            return_feedback = feedback_nodes.find(n => n.attr.feedback_type === 'incorrect_final');
        } else {
            // prioritize incorrect_prompt, and if it is not available, use incorrect_final (MOD-1948/MO.ELEC.3.0.0a - 514)
            return_feedback = feedback_nodes.find(n => n.attr.feedback_type === 'incorrect_prompt') || feedback_nodes.find(n => n.attr.feedback_type === 'incorrect_final');
        }

        if (return_feedback === undefined) {
            console.warn('A Feedback Node Was Not Found'); // This shouldn't ever be reached! It likely indicates an XML error.
        }

        return return_feedback;
    }

    onInteractionComplete(){
        const event_nodes = this.props.node.childrenNamed('event_node');
        event_nodes.forEach(function (event) {
            if (event.attr.name === 'oninteractioncomplete' ) {
                handleEvent(event);
            }
        });
    }

    checkGraphicalAnswer() {
        let {graphicNodes = [], answersSelected = {}, numOfAttempts = 0, mcCompleted = false, variable = null, output} = this.state;
        let feedbackNode = null;

        let selectedAnswerNode;
        selectedAnswerNode = graphicNodes.find(n => answersSelected[n.attr.id]);
        if (!selectedAnswerNode) {
            // No answer selected.
            return;
        }

        let incorrectAnswerNode;
        // if allowMultiSelect is true, (MO.ELEC.3.0.0a, 302)
        // we store incorrectAnswerNode if any graphicNode's selection status doesn't match with that graphicNode's "correct" attribute
        if (this.state.allowMultiSelect) {
            selectedAnswerNode = null; //If we allow multi-select, ensure the feedback for one particular node doesn't get played as noted above
            incorrectAnswerNode = graphicNodes.find(n => {
                const correct = Boolean(Number(n.attr.correct));
                return answersSelected[n.attr.id] !== correct;
            });
            // if allowMultiSelect is false,
            // we store incorrectAnswerNode iff the selected graphicNode doesn't match the graphic_answer "correct" attribute, AND that "correct" attribute is false
        } else {
            incorrectAnswerNode = graphicNodes.find(n => {
                const correct = Boolean(Number(n.attr.correct));
                return answersSelected[n.attr.id] !== correct && correct === false; // correct === false is needed in case there are multiple correct graphicNodes
            });
        }

        if (this.state.allowMultiSelect && !incorrectAnswerNode ||
            !this.state.allowMultiSelect && !incorrectAnswerNode && selectedAnswerNode ||
            this.state.acceptAnyAnswer && selectedAnswerNode
        ) {
            // Answer is correct.
            feedbackNode = this.fetchAnswerFeedbackNode(selectedAnswerNode, true);
            mcCompleted = true;
            /**
             * When there are more than 1 mc-nodes in a single page, book-content.js keeps track of which mcNodes
             * have been completed so that mc-node overlay mask is not displayed while all mc-nodes have not been
             * answered MOD-2410 (Carbon Footprint 3.1.1 & 3.2.1 p706,707,710,etc.)
             */
            const {setMcNodeToCompleted} = this.props;
            const {id} = this.state;
            const completedMcNodeKey = isEmpty(variable) ? id : `${id}-${variable}`;
            setMcNodeToCompleted(completedMcNodeKey);

            this.onInteractionComplete();

            if(!isEmpty(variable) && !isEmpty(selectedAnswerNode)) {
                // when we need to save a value as the variable, mc-node's output="text" (MO.APHY.3.0.5, 305)
                // when we need to save the index as the variable, mc-node's output="index"  (MO.IMMG.3.0.2, 119)
                if (output==='text'){
                    this.saveStudentAnswer(variable, selectedAnswerNode.val);
                } else if (output==='index') {
                    this.saveStudentAnswer(variable, selectedAnswerNode.index);
                }
                this.updateAnswerProgress();
            }
        } else if (this.hasMoreAttempts()) {
            feedbackNode = this.fetchAnswerFeedbackNode(selectedAnswerNode);
            numOfAttempts += 1;
        } else {
            // No more attempts - wrong answer.
            const {setMcNodeToCompleted} = this.props;
            const {id} = this.state;
            feedbackNode = this.fetchAnswerFeedbackNode(selectedAnswerNode, false);
            mcCompleted = true;

            const completedMcNodeKey = isEmpty(variable) ? id : `${id}-${variable}`;
            setMcNodeToCompleted(completedMcNodeKey);

            this.onInteractionComplete();

            /* NOTE: The XML has no flag to indicate if answers will be shown. In general, graphical answers nodes seem
            to yield answers most of the time. We'll try using attempts > 1 and see if that works in all cases. */
            if (this.state.attempts > 1) {
                const correctAnswers = this.getCorrectGraphicalAnswers(); // {nodeId1: T/F, nodeId2: T/F ...}
                this.setState({
                    answersSelected: correctAnswers
                });
            }
            if(!isEmpty(variable) && !this.state.allowMultiSelect) { // allowMultiSelect shouldn't be saving a variable, because we would need multiple variables
                const firstCorrectAnswerNode = graphicNodes.find(n => { // find first variable that is "correct". If there is none, then a variable isnt saved (user needs to select one)
                    return Boolean(Number(n.attr.correct));
                });

                if (firstCorrectAnswerNode && (!isEmpty(firstCorrectAnswerNode.val) ||!isEmpty(firstCorrectAnswerNode.index))){
                    if (output==='text'){
                        this.saveStudentAnswer(variable, selectedAnswerNode.val);
                    } else if (output==='index') {
                        this.saveStudentAnswer(variable, selectedAnswerNode.index);
                    }
                    this.updateAnswerProgress();
                }
            }

        }

        // find and run a "stop timer" event if mcCompleted
        // ideally all methods relevant to this mcnode should be run here,
        // but we need to find an instance where this is a necessary change
        if (mcCompleted) {
            const event_nodes = this.props.node.childrenNamed('event_node');
            event_nodes.forEach(function (event) {
                event.childrenNamed('method_node').forEach(function (method) {
                    const param = method.childWithAttribute('name', 'param');
                    const value = method.childWithAttribute('name', 'value');
                    if (param && param.attr.value === 'start' && value && value.attr.value === 'false') {
                        document.dispatchEvent(new CustomEvent('jre-timer-stop',  {detail: {target: method.attr.target}}));
                    }
                });
            });
        }

        // if allMcNodesCompleted not true, don't enable overlay mask
        const allMcNodesCompleted = this.areAllMcNodesCompleted();

        this.setState({
            mcCompleted,
            numOfAttempts,
            currentFeedbackItem: feedbackNode,
            overlayMask: allMcNodesCompleted,
        });
    }

    hasMoreAttempts() {
        const {numOfAttempts, attempts } = this.state;
        return numOfAttempts < (parseInt(attempts) - 1);
    }

    checkAnswer() {
        let {numOfAttempts = 0, mcCompleted = false, variable = null, output, acceptAnyAnswer} = this.state;
        let feedbackNode = null;
        let selectedAnswerNode = this.state.answerNodes.find(n => this.state.answersSelected[n.attr.id]);

        if (!selectedAnswerNode) {
            // No answer selected.
            return;
        }
        const correct = parseInt(selectedAnswerNode.attr.correct) || acceptAnyAnswer;

        if (correct) {
            const {setMcNodeToCompleted} = this.props;
            const {id} = this.state;

            feedbackNode = this.fetchAnswerFeedbackNode(selectedAnswerNode, true);
            mcCompleted = true;

            const completedMcNodeKey = isEmpty(variable) ? id : `${id}-${variable}`;
            setMcNodeToCompleted(completedMcNodeKey);

            this.onInteractionComplete();
        } else if (this.hasMoreAttempts()) {
            numOfAttempts += 1;
            feedbackNode = this.fetchAnswerFeedbackNode(selectedAnswerNode);
        } else {
            // No more attempts - wrong answer.
            const {setMcNodeToCompleted} = this.props;
            const {id} = this.state;

            feedbackNode = this.fetchAnswerFeedbackNode(selectedAnswerNode, false);
            mcCompleted = true;

            const completedMcNodeKey = isEmpty(variable) ? id : `${id}-${variable}`;
            setMcNodeToCompleted(completedMcNodeKey);

            this.onInteractionComplete();

            // set selected answer to the first correct one in order to save a variable
            selectedAnswerNode = this.state.answerNodes.find(n => {
                return Boolean(Number(n.attr.correct));
            });
            numOfAttempts += 1; // we still update numOfAttempts for changing feedback-item-node's key, in case feedbackNode is the same for incorrect_final
        }

        if(mcCompleted) {
            // when we need to save a value as the variable, mc-node's output="text" (MO.APHY.3.0.5, 305)
            // when we need to save the index as the variable, mc-node's output="index"  (MO.IMMG.3.0.2, 119)
            if(!isEmpty(variable) && !isEmpty(selectedAnswerNode)) {
                if (output === 'text') {
                    this.saveStudentAnswer(variable, selectedAnswerNode.val);
                } else if (output === 'index') {
                    this.saveStudentAnswer(variable, selectedAnswerNode.index);
                }
                this.updateAnswerProgress();
            }
        }

        // if allMcNodesCompleted not true, don't enable overlay mask
        const allMcNodesCompleted = this.areAllMcNodesCompleted();

        this.setState({
            currentFeedbackItem: feedbackNode,
            overlayMask: allMcNodesCompleted,
            numOfAttempts,
            mcCompleted
        });
    }

    answerSelected(nodeId) {
        let { answersSelected = {}, allowMultiSelect } = this.state;
        // In the case that we do not allow multiSelect, we will flip all answersSelected to False.
        if (!allowMultiSelect) {
            for (let _key in answersSelected) {
                answersSelected[_key] = false;
            }
        }
        // Now we need to flip the state of the answer selected.
        if (!isEmpty(this.state.graphicNodes)){
            this.showGraphicAnswerImage(nodeId);
        }

        answersSelected[nodeId] = !answersSelected[nodeId];
        this.setState({
            answersSelected: answersSelected
        });
    }

    showGraphicAnswerImage(nodeId) {
        // This method is used to handle cases where selecting a MC input renders an image
        // in the known case the event_node was nested in graphic_answer_node, which along with other
        // graphic_answer_node were nested in a graphic_answers_node, which in turn was nested in a mc_node
        const graphic_answer_node = this.state.graphicNodes.find( correct_graphic_answer_node => {
            return correct_graphic_answer_node .attr.id === nodeId;
        });
        const event_node_list = graphic_answer_node.childrenNamed('event_node');
        event_node_list.forEach(event_node => {
            if(event_node.attr.name === 'onclick'){
                handleEvent(event_node);
            }
        });
    }

    getCorrectGraphicalAnswers() {
        return this.state.graphicNodes.reduce(
            (answer, n) => {
                return {...answer, [n.attr.id]: Boolean(Number(n.attr.correct))};
            }, {}
        );
    }

    areAllMcNodesCompleted() {
        /**
         * When props is updated by book-content, we check if all mcNodes have been completed, loop through object,
         * push to array so we can perform .every evaluation in return
         * */
        const {completedMcNodes} = this.props;
        const booleanCompletedMcNodesList = [];
        for(const mcNodeId in completedMcNodes){
            if(completedMcNodes.hasOwnProperty(mcNodeId)){
                booleanCompletedMcNodesList.push(completedMcNodes[mcNodeId]);
            }
        }

        return booleanCompletedMcNodesList.every(isCompleted => {
            return isCompleted === true;
        });
    }

    render() {
        const { moduleURL = '', sessionNum = 0, pageNum = 0} = this.props;
        const {
            numOfAttempts, //current attempts
            mcType,
            graphicAnswerNodeZIndex = 1,    // MOD-1112 but it only partially fix issue, makes it fully functional
            // but not visually identical to FRE
        } = this.state;

        let checkAnswer = null;
        if (mcType === 'answerNode') {
            checkAnswer = this.checkAnswer;
        } else {
            checkAnswer = this.checkGraphicalAnswer;
        }

        const listItems = this.props.node.children.map(n => {
            switch (n.name) {
                case 'question_node':
                    return <QuestionNode key={n.attr.id} node={n}/>;
                case 'answers_node':
                    return <AnswersNode key={n.attr.id} node={n}
                                        answersSelected={this.state.answersSelected}
                                        onAnswerSelected={this.answerSelected.bind(this)}/>;
                case 'graphic_answers_node':
                    return n.children.map(n => {
                        if (n.name === 'graphic_answer_node'){
                            return <GraphicAnswerNode
                                selected={this.state.answersSelected[n.attr.id]}
                                onGraphicSelected={this.answerSelected.bind(this, n.attr.id)}
                                node={n}
                                moduleURL={moduleURL}
                                key={n.attr.id}
                                zIndex={graphicAnswerNodeZIndex}
                            />;
                        }
                        return null; // if n.name !== 'graphic_answer_node' return null
                    });
                case 'button_node':
                    return <ButtonNode
                        moduleURL={moduleURL}
                        onClick={checkAnswer.bind(this)}
                        key={`${sessionNum}-${pageNum}-${n.attr.id}`}
                        node={n}
                    />;
                default:
                    return null;
            }
        });

        return (
            <div className="mc-node-container">
                <div className={this.state.overlayMask ? 'overlay-mask' : ''} />
                <div>
                    {listItems}
                </div>
                {
                    this.state.currentFeedbackItem &&
                    <div className="node feedback-item">
                        <FeedbackItem
                            moduleURL={moduleURL}
                            node={getDataFromFeedbackNode(this.state.currentFeedbackItem)}
                            onAudioEnded={this.feedbackAudioEnded.bind(this)}
                            key={`${this.state.currentFeedbackItem.attr.id}-${numOfAttempts}`}
                        />
                    </div>
                }
            </div>
        );
    }
}

McNode.propTypes = {
    node: PropTypes.object.isRequired
};

const mapStateToProps = (state) => {
    return {
        answers: state.answers.list,
        users:state.auth.users
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        saveAnswer: (answer,users) => {
            dispatch(saveAnswer(answer,users));
        },
        formatErrors: (err, redirectTo) => dispatch(formatErrors(err, redirectTo)),
    };
};

const McNodeContainer = connect(
    mapStateToProps,
    mapDispatchToProps
)(McNode);

export default McNodeContainer;



// WEBPACK FOOTER //
// ./src/js/components/app/jre/elements/mc-node.js